import { Link } from "react-router-dom";
import type { MonthlyPlanningSummary } from "../../entities/ledger/types";
import { decimal, money } from "../../shared/lib/format";

interface BudgetHeroCardProps {
  summary?: MonthlyPlanningSummary;
  loading?: boolean;
}

// The overview headline: this month's budgets against what was actually
// spent against them. Every figure is the server's own planning summary, so
// this card and the planning page can never disagree. Budgets are the single
// limit concept and are set up on the planning page; this card only reports.
export function BudgetHeroCard({ summary, loading = false }: BudgetHeroCardProps) {
  if (loading || !summary) {
    return (
      <article className="panel budget-card" aria-labelledby="budget-hero-title">
        <div className="card-label"><span id="budget-hero-title">Monthly spending</span></div>
        <p className="hero-empty">{loading ? "Loading this month's budgets…" : "Budgets are unavailable right now."}</p>
      </article>
    );
  }

  const { currency } = summary;
  if (summary.budgetCount === 0) {
    return (
      <article className="panel budget-card" aria-labelledby="budget-hero-title">
        <div className="card-label"><span id="budget-hero-title">Monthly spending</span></div>
        <div className="hero-amount"><span>{decimal(summary.actualExpenses.minor, currency)}</span><small>{currency} spent</small></div>
        <p className="hero-empty">No budget for this month yet. A budget is a spending limit over categories; what you actually spent is derived from the journal.</p>
        <Link className="panel-link" to="/planning/budgets">Set a budget</Link>
      </article>
    );
  }

  const isOver = summary.budgetRemaining.minor < 0;
  const percent = summary.budgetLimit.minor > 0
    ? Math.min(100, (summary.budgetSpent.minor / summary.budgetLimit.minor) * 100)
    : 0;

  return (
    <article className="panel budget-card" aria-labelledby="budget-hero-title">
      <div className="card-label">
        <span id="budget-hero-title">Monthly spending · remaining</span>
        <span className="hero-tools">
          <span className={`status-pill ${isOver ? "over" : ""}`}>{isOver ? "Over limit" : "On track"}</span>
        </span>
      </div>
      <div className="hero-amount"><span>{decimal(summary.budgetRemaining.minor, currency)}</span><small>{currency}</small></div>
      <div className="progress-track" role="progressbar" aria-label="Budget used"
        aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(percent)}>
        <span className={isOver ? "over" : ""} style={{ width: `${isOver ? 100 : percent}%` }} />
      </div>
      <div className="budget-meta">
        <div><span>Spent against budgets</span><strong>{money(summary.budgetSpent.minor, currency)}</strong></div>
        <div><span>Limit</span><strong>{money(summary.budgetLimit.minor, currency)}</strong></div>
        <div><span>Days left</span><strong>{summary.daysRemaining}</strong></div>
      </div>
      <Link className="panel-link" to="/planning/budgets">
        {summary.budgetCount === 1 ? "Manage this budget" : `Manage ${summary.budgetCount} budgets`}
      </Link>
    </article>
  );
}
